import React from 'react';
import { Modal, Tag, Button, Divider, message } from 'antd';
import {
  FileTextOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  SendOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { submitReportToRegulator } from '../../services/regulatoryReportingService';

const STATUS_CONFIG = {
  DRAFT: { color: 'default', label: 'Draft' },
  PENDING_APPROVAL: { color: 'gold', label: 'Pending Approval' },
  APPROVED: { color: 'green', label: 'Approved' },
  REJECTED: { color: 'red', label: 'Rejected' },
  SUBMITTED: { color: 'blue', label: 'Submitted' },
  ARCHIVED: { color: 'purple', label: 'Archived' },
};

const REPORT_TYPE_LABELS = {
  CBN_RETURNS: 'CBN Returns',
  NDIC_RETURNS: 'NDIC Returns',
  AML_CTR: 'AML — Currency Transaction Report',
  AML_SAR: 'AML — Suspicious Activity Report',
  AML_STR: 'AML — Suspicious Transaction Report',
  NFIU_REPORT: 'NFIU Report',
  EFCC_REPORT: 'EFCC Compliance Report',
  CREDIT_BUREAU: 'Credit Bureau Report',
  CAPITAL_ADEQUACY: 'Capital Adequacy Report',
  LIQUIDITY_REPORT: 'Liquidity Report',
  PRUDENTIAL_RETURNS: 'Prudential Returns',
  RISK_ASSESSMENT: 'Risk Assessment Report',
  KYC_COMPLIANCE: 'KYC Compliance Report',
  TRANSACTION_MONITORING: 'Transaction Monitoring Report',
  CUSTOM: 'Custom Report',
};

const ReportDetailModal = ({ visible, onClose, record, onRefresh }) => {
  if (!record) return null;

  const statusCfg = STATUS_CONFIG[record.status] || { color: 'default', label: record.status };

  const handleSubmit = async () => {
    try {
      const res = await submitReportToRegulator(record.id);
      if (res?.status) {
        message.success('Report submitted to regulator successfully');
        onRefresh?.();
        onClose();
      } else {
        message.error(res?.message || 'Failed to submit report');
      }
    } catch (err) {
      console.error('Submit failed:', err);
      message.error('An error occurred while submitting');
    }
  };

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
          <FileTextOutlined style={{ color: '#ff6700' }} />
          Report Details
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={
        record.status === 'APPROVED' ? (
          <Button
            icon={<SendOutlined />}
            className="reg-action-submit"
            size="large"
            onClick={handleSubmit}
          >
            Submit to Regulator
          </Button>
        ) : null
      }
      width={720}
      className="reg-detail-modal"
      centered
    >
      {/* ── Report Information ──────────────── */}
      <div className="reg-detail-section-title">
        <InfoCircleOutlined /> Report Information
      </div>
      <div className="reg-detail-grid">
        <div className="reg-detail-item">
          <span className="reg-detail-label">
            <FileTextOutlined /> Report Name
          </span>
          <span className="reg-detail-value" style={{ fontWeight: 600 }}>
            {record.reportName || '—'}
          </span>
        </div>
        <div className="reg-detail-item">
          <span className="reg-detail-label">Report Type</span>
          <span className="reg-detail-value">
            {REPORT_TYPE_LABELS[record.reportType] || record.reportType || '—'}
          </span>
        </div>
        <div className="reg-detail-item">
          <span className="reg-detail-label">
            <CalendarOutlined /> Reporting Period
          </span>
          <span className="reg-detail-value">
            {record.startDate && record.endDate
              ? `${moment(record.startDate).format('MMM DD, YYYY')} — ${moment(record.endDate).format('MMM DD, YYYY')}`
              : '—'}
          </span>
        </div>
        <div className="reg-detail-item">
          <span className="reg-detail-label">Status</span>
          <span className="reg-detail-value">
            <Tag
              color={statusCfg.color}
              className="reg-status-tag"
            >
              {statusCfg.label}
            </Tag>
          </span>
        </div>
      </div>

      <Divider dashed style={{ margin: '20px 0' }} />

      {/* ── Generation Details ──────────────── */}
      <div className="reg-detail-section-title">
        <ClockCircleOutlined /> Generation Details
      </div>
      <div className="reg-detail-grid">
        <div className="reg-detail-item">
          <span className="reg-detail-label">
            <ClockCircleOutlined /> Generation Date
          </span>
          <span className="reg-detail-value">
            {record.generatedAt
              ? moment(record.generatedAt).format('MMM DD, YYYY • hh:mm A')
              : '—'}
          </span>
        </div>
        <div className="reg-detail-item">
          <span className="reg-detail-label">
            <UserOutlined /> Generated By
          </span>
          <span className="reg-detail-value" style={{ fontWeight: 600 }}>
            {record.generatedBy || '—'}
          </span>
        </div>
        {record.approvedAt && (
          <div className="reg-detail-item">
            <span className="reg-detail-label">
              <CheckCircleOutlined /> Approved Date
            </span>
            <span className="reg-detail-value">
              {moment(record.approvedAt).format('MMM DD, YYYY • hh:mm A')}
            </span>
          </div>
        )}
        {record.approvedBy && (
          <div className="reg-detail-item">
            <span className="reg-detail-label">
              <UserOutlined /> Approved By
            </span>
            <span className="reg-detail-value">{record.approvedBy}</span>
          </div>
        )}
        {record.submittedAt && (
          <div className="reg-detail-item">
            <span className="reg-detail-label">
              <SendOutlined /> Submitted Date
            </span>
            <span className="reg-detail-value">
              {moment(record.submittedAt).format('MMM DD, YYYY • hh:mm A')}
            </span>
          </div>
        )}
        {record.submittedBy && (
          <div className="reg-detail-item">
            <span className="reg-detail-label">
              <UserOutlined /> Submitted By
            </span>
            <span className="reg-detail-value">{record.submittedBy}</span>
          </div>
        )}
      </div>

      {/* ── Description ──────────────── */}
      {record.description && (
        <>
          <Divider dashed style={{ margin: '20px 0' }} />
          <div className="reg-detail-section-title">
            <InfoCircleOutlined /> Description
          </div>
          <p style={{ color: '#595959', fontSize: 13, lineHeight: 1.8, margin: 0 }}>
            {record.description}
          </p>
        </>
      )}

      {/* ── Rejection Reason ──────────────── */}
      {record.status === 'REJECTED' && record.rejectionReason && (
        <>
          <Divider dashed style={{ margin: '20px 0' }} />
          <div className="reg-detail-section-title" style={{ color: '#ff4d4f' }}>
            <InfoCircleOutlined /> Rejection Reason
          </div>
          <p style={{ color: '#ff4d4f', fontSize: 13, lineHeight: 1.8, margin: 0, fontWeight: 500 }}>
            {record.rejectionReason}
          </p>
        </>
      )}
    </Modal>
  );
};

export default ReportDetailModal;
